import * as React from 'react';
import { useSelector } from 'react-redux';
import { useIsDesktop } from '@cian/valuation-utils-component';
import { useDeviceType } from '@cian/ui-kit/responsive';

import { MainScreenMobile } from '../../components/MainScreen';
import { ProgressBarContainer } from '../ProgressBarContainer';
import { Provider, useFormController, FormContainer } from '../FormContainer';
import { BackToPageContainer } from '../BackToPageContainer';
import { TFormStoreData } from '../../types/form';
import { submitForm } from '../../actions/submitForm';
import { useOnRenderedInBrowser } from './utils/useOnRenderedInBrowser';
import { PageBaseLayout } from '../../components/PageBaseLayout';
import { showGoBackBtn } from './utils/showGoBackBtn';
import { IntroContainer } from '../IntroContainer';
import { MainScreenDesktopContainer } from '../MainScreenDesktopContainer';
import { selectWithIntroScreen } from '../../selectors/selectWithIntroScreen';
import { useSmartContactPixel } from './utils/useSmartContactPixel';
import { selectUser } from '../../selectors/selectUser';

interface IPageContainerWithDataProps {
  formData: TFormStoreData;
}

export const PageContainerWithData: React.FC<React.PropsWithChildren<IPageContainerWithDataProps>> = ({ formData }) => {
  const formContext = useFormController(formData, submitForm);
  const deviceType = useDeviceType();
  const { setPrevStep, quizStarted, quizCompleted, stepsHistory } = formContext;
  const isDesktop = useIsDesktop();
  const withIntroScreen = useSelector(selectWithIntroScreen(isDesktop));
  const { isAuthenticatedByRedirect } = useSelector(selectUser);

  const MainScreen = isDesktop ? MainScreenDesktopContainer : MainScreenMobile;
  useSmartContactPixel(quizCompleted);

  useOnRenderedInBrowser(formData);

  const main = React.useMemo(() => {
    if (quizCompleted) {
      return <FormContainer />;
    }

    return (
      <MainScreen
        main={!isAuthenticatedByRedirect && withIntroScreen && !quizStarted ? <IntroContainer /> : <FormContainer />}
        backToPage={<BackToPageContainer />}
        progressBar={quizStarted || isDesktop ? <ProgressBarContainer /> : null}
        showGoBackBtn={showGoBackBtn(stepsHistory)}
        handleGoBack={setPrevStep}
        goBackText="Назад"
      />
    );
  }, [quizCompleted, MainScreen, withIntroScreen, quizStarted, isDesktop, stepsHistory, setPrevStep]);

  return (
    <Provider value={formContext}>
      <PageBaseLayout quizCompleted={formContext.quizCompleted} deviceType={deviceType}>
        {main}
      </PageBaseLayout>
    </Provider>
  );
};

PageContainerWithData.displayName = 'PageContainerWithData';
